import { useRef, useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useQueryClient } from '@tanstack/react-query'
import { showToast } from '../../lib/toastStore'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScannedItem {
  name:      string
  emoji:     string
  quantity:  string
  freshness: string
  category:  string
  location:  string
}

interface ScanResult {
  location_type?: string
  summary?:       string
  items:          ScannedItem[]
  error?:         string
}

interface Props {
  householdId: string
  onComplete:  (items: ScannedItem[]) => void
  onClose:     () => void
}

type Step = 'camera' | 'scanning' | 'results' | 'saving'

const CATEGORY_MAP: Record<string, string> = {
  produce:  'fresh_produce',
  dairy:    'dairy',
  meat:     'meat_fish',
  bakery:   'bread_bakery',
  frozen:   'frozen',
  cleaning: 'pantry_staple',
  pantry:   'pantry_staple',
  other:    'pantry_staple',
}

const SCAN_TYPES = [
  { id: 'auto',    label: 'Auto',    emoji: '🔍' },
  { id: 'fridge',  label: 'Fridge',  emoji: '🧊' },
  { id: 'freezer', label: 'Freezer', emoji: '❄️' },
  { id: 'pantry',  label: 'Pantry',  emoji: '🏠' },
  { id: 'cabinet', label: 'Cabinet', emoji: '🪟' },
]

// ─── KitchenScanner ───────────────────────────────────────────────────────────

export function KitchenScanner({ householdId, onComplete, onClose }: Props) {
  const videoRef   = useRef<HTMLVideoElement>(null)
  const canvasRef  = useRef<HTMLCanvasElement>(null)
  const streamRef  = useRef<MediaStream | null>(null)
  const queryClient = useQueryClient()

  const [scanType,          setScanType]          = useState('auto')
  const [step,              setStep]              = useState<Step>('camera')
  const [captured,          setCaptured]          = useState<string | null>(null)
  const [results,           setResults]           = useState<ScanResult | null>(null)
  const [error,             setError]             = useState<string | null>(null)
  const [selectedItems,     setSelectedItems]     = useState<number[]>([])
  const [allDetectedItems,  setAllDetectedItems]  = useState<ScannedItem[]>([])
  const [scanCount,         setScanCount]         = useState(0)

  useEffect(() => {
    startCamera()
    return () => stopCamera()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch {
      setError('Camera not available. Please allow camera access.')
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop())
  }

  function handleCapture() {
    if (!videoRef.current || !canvasRef.current) return

    const video  = videoRef.current
    const canvas = canvasRef.current

    const maxSize = 1280
    let w = video.videoWidth
    let h = video.videoHeight

    if (w > maxSize) {
      h = Math.round(h * maxSize / w)
      w = maxSize
    }

    canvas.width  = w
    canvas.height = h

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0, w, h)

    const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
    const base64  = dataUrl.split(',')[1]

    setCaptured(dataUrl)
    stopCamera()
    analyzeImage(base64)
  }

  async function analyzeImage(base64: string) {
    setStep('scanning')
    setError(null)

    try {
      const response = await fetch('/api/claude-fridge', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ imageBase64: base64, mediaType: 'image/jpeg', scanType }),
      })

      const data: ScanResult = await response.json()

      if (data.error || !data.items?.length) {
        setError(data.error || 'No items detected. Try better lighting and hold steady.')
        setStep('camera')
        setCaptured(null)
        startCamera()
        return
      }

      // Merge with previous scan results, deduplicating by name
      const newItems = data.items.filter(newItem =>
        !allDetectedItems.some(
          existing => existing.name.toLowerCase() === newItem.name.toLowerCase()
        )
      )

      const merged = [...allDetectedItems, ...newItems]
      setAllDetectedItems(merged)
      setScanCount(prev => prev + 1)
      setResults({ ...data, items: merged })
      setSelectedItems(merged.map((_, i) => i))
      setStep('results')
    } catch {
      setError('Could not analyze image. Try again.')
      setStep('camera')
      setCaptured(null)
      startCamera()
    }
  }

  async function handleConfirm() {
    if (!results?.items?.length) return
    setStep('saving')

    const itemsToAdd = selectedItems.map(i => results.items[i])

    try {
      for (const item of itemsToAdd) {
        const { data: existing } = await supabase
          .from('pantry_items')
          .select('id')
          .eq('household_id', householdId)
          .ilike('name', item.name)
          .maybeSingle()

        if (existing) {
          await supabase
            .from('pantry_items')
            .update({ location: item.location || 'fridge', status_override: null })
            .eq('id', existing.id)
        } else {
          const freshnessCategory = CATEGORY_MAP[item.category] || 'pantry_staple'
          await supabase.from('pantry_items').insert({
            household_id:       householdId,
            name:               item.name.charAt(0).toUpperCase() + item.name.slice(1),
            emoji:              item.emoji || '📦',
            display_category:   item.category || 'other',
            freshness_category: freshnessCategory,
            
            is_active:          true,
            confirmation_count: 0,
            location:           item.location || 'fridge',
            track_expiration:   ['fresh_produce', 'dairy', 'meat_fish'].includes(freshnessCategory),
            status_override:    null,
          })
        }
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      showToast(`${itemsToAdd.length} items added to pantry ✓`)
      onComplete(itemsToAdd)
    } catch {
      showToast('Error saving items')
      setStep('results')
    }
  }

  function toggleItem(index: number) {
    setSelectedItems(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 shrink-0">
        <button
          onClick={onClose}
          className="text-white text-sm bg-white/20 px-4 py-2 rounded-full"
        >
          Cancel
        </button>
        <p className="text-white text-sm font-semibold">Kitchen Scanner</p>
        <div className="w-16" />
      </div>

      {/* Scan type selector */}
      {step === 'camera' && (
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto shrink-0">
          {SCAN_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setScanType(type.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                          whitespace-nowrap shrink-0 transition-all ${
                scanType === type.id ? 'bg-white text-[#2C2A26]' : 'bg-white/20 text-white'
              }`}
            >
              <span>{type.emoji}</span>
              {type.label}
            </button>
          ))}
        </div>
      )}

      {/* Camera / scanning view */}
      {(step === 'camera' || step === 'scanning') && (
        <div className="flex-1 relative">
          {!captured ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-6 border-2 border-white/50 rounded-2xl
                              pointer-events-none flex items-center justify-center">
                <p className="text-white/60 text-xs text-center px-8">
                  Point at your {scanType === 'auto' ? 'fridge, shelf or cabinet' : scanType}
                </p>
              </div>
            </>
          ) : (
            <img src={captured} className="absolute inset-0 w-full h-full object-cover" />
          )}

          {step === 'scanning' && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-full border-2 border-white border-t-transparent animate-spin" />
              <p className="text-white font-medium">
                Scanning your {scanType === 'auto' ? 'kitchen' : scanType}…
              </p>
            </div>
          )}
        </div>
      )}

      {/* Capture button */}
      {step === 'camera' && (
        <div className="px-4 pb-8 pt-4 text-center shrink-0">
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          {scanCount > 0 && (
            <p className="text-white/60 text-xs mb-3">
              {allDetectedItems.length} items found across {scanCount} scan{scanCount !== 1 ? 's' : ''}
            </p>
          )}
          <button
            onClick={handleCapture}
            className="w-20 h-20 rounded-full bg-white mx-auto flex items-center justify-center
                       shadow-lg active:scale-95 transition-transform"
          >
            <div className="w-16 h-16 rounded-full bg-[#6B9E78]" />
          </button>
          <p className="text-white/60 text-xs mt-3">Tap to scan</p>
        </div>
      )}

      {/* Results */}
      {step === 'results' && results && (
        <div className="flex-1 bg-white rounded-t-3xl overflow-hidden flex flex-col">

          {/* Results header */}
          <div className="px-5 pt-5 pb-3 border-b border-[#F0EDE8] shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-bold text-base text-[#2C2A26] capitalize">
                  {results.location_type || scanType} scan
                </p>
                <p className="text-xs text-[#7A7468]">
                  {results.items.length} items detected across {scanCount} scan{scanCount !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => { setCaptured(null); setStep('camera'); startCamera() }}
                className="text-xs text-[#7A7468] border border-[#E8E2DC] px-3 py-1.5 rounded-full"
              >
                + Scan more
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedItems(results.items.map((_, i) => i))}
                className="text-xs font-medium text-[#6B9E78]"
              >
                Select all
              </button>
              <span className="text-[#E8E2DC]">|</span>
              <button
                onClick={() => setSelectedItems([])}
                className="text-xs text-[#7A7468]"
              >
                Clear
              </button>
              <span className="text-xs text-[#9A9491] ml-auto">
                {selectedItems.length} selected
              </span>
            </div>
          </div>

          {/* Items list */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {results.items.map((item, index) => (
              <button
                key={index}
                onClick={() => toggleItem(index)}
                className={`w-full flex items-center gap-3 py-3 border-b border-[#F0EDE8]
                            text-left last:border-0 transition-opacity ${
                  selectedItems.includes(index) ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                  selectedItems.includes(index)
                    ? 'border-[#6B9E78] bg-[#6B9E78]'
                    : 'border-[#D0CBC5]'
                }`}>
                  {selectedItems.includes(index) && (
                    <span className="text-white text-[10px]">✓</span>
                  )}
                </div>

                <span className="text-xl shrink-0">{item.emoji || '📦'}</span>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C2A26] truncate">{item.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      item.freshness === 'fresh' || item.freshness === 'good'
                        ? 'bg-[#E8F2EA] text-[#6B9E78]'
                        : item.freshness === 'expiring'
                        ? 'bg-[#FAEAE8] text-[#C25B4E]'
                        : 'bg-[#F2EDE6] text-[#7A7468]'
                    }`}>
                      {item.freshness || 'ok'}
                    </span>
                    <span className="text-[10px] text-[#9A9491] capitalize">
                      → {item.location || 'fridge'}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Confirm button */}
          <div className="px-5 pb-8 pt-3 border-t border-[#F0EDE8] shrink-0">
            <button
              onClick={handleConfirm}
              disabled={selectedItems.length === 0}
              className="w-full py-4 rounded-2xl text-base font-semibold text-white disabled:opacity-40"
              style={{ background: '#6B9E78' }}
            >
              Add {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} to Pantry →
            </button>
          </div>
        </div>
      )}

      {/* Saving */}
      {step === 'saving' && (
        <div className="flex-1 bg-white rounded-t-3xl flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin mx-auto mb-4" />
            <p className="text-sm font-medium text-[#2C2A26]">Adding items to pantry…</p>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  )
}
